import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../lib/api';

interface ReferralStats {
  referralCode: string;
  referralLink: string;
  totalReferred: number;
  pointsEarned: number;
}

export default function ReferralCard() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/api/mundus/referral/stats');
      setStats(res.data);
    } catch {
      // No referral data
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!stats) return;
    try {
      await navigator.clipboard.writeText(stats.referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for mobile
      const input = document.createElement('input');
      input.value = stats.referralLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    if (!stats) return;
    const text = encodeURIComponent(
      `Hey! I'm using Mundus Novus — an AI agent that helps me get things done. Try it free and we both earn 50 bonus points!\n\n${stats.referralLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading || !stats) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900">Invite Friends</h3>
        {stats.totalReferred > 0 && (
          <span className="text-xs text-blue-600 font-medium">
            {stats.totalReferred} referred &middot; +{stats.pointsEarned} pts
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Earn 50 points for each friend who completes their first win
      </p>

      {/* Referral code display */}
      <div className="flex items-center gap-2 mb-3">
        <code className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg text-slate-700 truncate">
          {stats.referralLink}
        </code>
        <button
          onClick={copyLink}
          className="px-3 py-2 text-xs font-medium bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
        >
          {copied ? '✓' : 'Copy'}
        </button>
      </div>

      {/* Share buttons */}
      <div className="flex gap-2">
        <button
          onClick={shareWhatsApp}
          className="flex-1 px-3 py-2 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5"
        >
          <span>💬</span> Share on WhatsApp
        </button>
        <button
          onClick={copyLink}
          className="flex-1 px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Copy Link
        </button>
      </div>
    </div>
  );
}
